import React, {useEffect, useState} from "react";
import TextEdit from "../../organisms/textEdit/textEdit";
import {observer} from "mobx-react-lite";
import NoticeItem from "../../../stores/domainStore/pages/models/noticeItem";
import {useTranslation} from "react-i18next";
import "./createEditNoticeTemplate.sass";
import CheckBox from "../../atoms/checkBox/checkBox";
import {NoticeStatus} from "../../../shared/enums/noticeStatus";
import CreateEditForm from "../../molecules/createEditForm/createEditForm";
import Skeleton from "react-loading-skeleton";
import ButtonIcon from "../../atoms/button/buttonIcon";
import {useHistory} from "react-router";
import {Action, Path} from "history";
import {useStores} from "../../../stores/useStores";
import {RoutePaths} from "../../../shared/routePaths";
import MultiVillageSelect from "../../molecules/multiVillageSelect/multiVillageSelect";
import {Col, Row} from "reactstrap";
import {Navigation} from "../../../utils/navigation";
import RichTextEditor from "../../atoms/richTextEditor/richTextEditor";
import RadioGroup from "../../atoms/radioGroup/radioGroup";
import {ObservableList} from "../../../stores/observables/observableList";
import {RadioGroupKeyValue} from "../../../stores/observables/radioGroupKeyValues/radioGroupKeyValue";
import {SimpleRadioGroupKeyValue} from "../../../stores/observables/radioGroupKeyValues/simpleRadioGroupKeyValue";
import {runInAction} from "mobx";
import {NoticePublishType} from "../../../shared/enums/noticePublishType";
import {DateTimePickerRadioGroupKeyValue} from "../../../stores/observables/radioGroupKeyValues/dateTimePickerRadioGroupKeyValue";
import {zonedTimeToUtc} from "date-fns-tz";
import {LayoutOrientation} from "../../../shared/enums/layoutOrientation";
import {AuthUser} from "../../../utils/auth/user";
import {NoticeType} from "../../../shared/enums/noticeType";
import ImagePreviewWithEditor from "../../organisms/imagePreviewWithEditor/imagePreviewWithEditor";
import {BreadcrumbItem} from "../../atoms/breadcrumb/breadcrumb";
import DateTimePicker from "../../molecules/dateTimePicker/dateTimePicker";
import {useVillageFeatureToggle} from "../../../utils/featureToggle/villageFeatureToggle";
import {ServicedApartmentResidentsFeatureName} from "../../../shared/constants";

export interface ICreateEditNoticesTemplateProps {
  notice: NoticeItem;
  publishAction: () => Promise<void>;
  isSaving: boolean;
  isLoading: boolean;
  saveDraftAction: () => void;
  hasUnsavedChanges?: boolean;
}

const CreateEditNoticeTemplate: React.FC<ICreateEditNoticesTemplateProps> = ({
  notice,
  publishAction,
  isSaving,
  isLoading,
  saveDraftAction,
  hasUnsavedChanges
}) => {
  const {
    uiStore: {modalStore, toastMessageStore, accountStore},
    domainStore: {noticesDomainStore}
  } = useStores();
  const [buttonClicked, setButtonClicked] = useState("");

  const {t} = useTranslation(["translation", "notice"]);
  const isEdit = notice.id !== "";
  const isDraft = !notice.id || notice.status === NoticeStatus.Draft;
  const isSuperUser = AuthUser.getInstance().IsSuperUser;

  // Audience targeting is only relevant on the multi-village calendar or in a
  // single village that has the Serviced Apartment residents feature enabled.
  const hasServicedApartmentResidents = useVillageFeatureToggle(
    ServicedApartmentResidentsFeatureName
  ).hasFeature.value;
  const showAudienceSelection =
    notice.isMultiVillage || hasServicedApartmentResidents;
  const showAudienceError = !notice.isPersonTypeSelectionValid;
  const isAudienceSelectionDisabled =
    isLoading || (isEdit && notice.status === NoticeStatus.Published);

  const noticeTypeHelpText = (() => {
    switch (notice.noticeType.text) {
      case NoticeType.General:
        return t("notice:messages.noticeType.generalHelpText");
      case NoticeType.Important:
        return t("notice:messages.noticeType.importantHelpText");
      case NoticeType.Announcement:
        return t("notice:messages.noticeType.announcementHelpText");
    }
  })();

  const onButtonClick = (buttonName: "publish" | "saveDraft") => {
    setButtonClicked(buttonName);
    switch (buttonName) {
      case "saveDraft":
        saveDraftAction();
        break;

      case "publish":
        publishAction();
        break;
    }
  };

  const actionButtons = (
    <>
      {isDraft && (
        <ButtonIcon
          type="secondary"
          onClick={() => {
            if (notice.canSaveDraft) {
              onButtonClick("saveDraft");
            }
          }}
          isDisabled={
            !notice.canSaveDraft || !notice.hasChanges || isSaving || isLoading
          }
          isBusy={buttonClicked === "saveDraft" && isSaving}
        >
          {t("translation:actions.saveDraft")}
        </ButtonIcon>
      )}

      <ButtonIcon
        type="primary"
        onClick={() => {
          notice.setIsDirty(true);
          if (notice.canPublish) {
            onButtonClick("publish");
          }
        }}
        isDisabled={
          ((notice.status === NoticeStatus.Published ||
            notice.status === NoticeStatus.Scheduled ||
            (notice.isMultiVillage && notice.status === NoticeStatus.Draft)) &&
            !notice.canPublish) ||
          isSaving ||
          isLoading
        }
        isBusy={buttonClicked === "publish" && isSaving}
      >
        {t("translation:actions.publish")}
      </ButtonIcon>
    </>
  );

  const history = useHistory();
  React.useEffect(() => {
    const unblock = history.block((path: Path, action: Action) => {
      if (hasUnsavedChanges) {
        // If the modal is open and the user hit the back button on the browser, unblock the navigation.
        if (modalStore.isOpen && action === Action.Pop) {
          unblock();
          return true;
        }
        modalStore.openModalWithContent({
          content: t("translation:messages.cancelCreateOrEdit", {
            target: t("notice:notice")
          }),
          title: t("translation:titles.unsavedChanges"),
          primaryButtonText: t("translation:confirmations.affirmativeLeave"),
          secondaryButtonText: t("translation:confirmations.negative"),
          canToggle: false,
          dangerButton: true,
          centered: true,
          onPrimaryClick: () => {
            unblock();
            history.push(path);
            modalStore.toggle();
          },
          onSecondaryClick: modalStore.toggle
        });
        return false;
      }
    });
    return () => {
      unblock();
    };
  }, [hasUnsavedChanges, history, isEdit, modalStore, t]);

  const timezone = noticesDomainStore.getTimeZone();

  useEffect(() => {
    noticesDomainStore.resetEditableNotice();
  }, [noticesDomainStore, timezone]);

  // Clearing policy checkbox when file is removed.
  useEffect(() => {
    if (!notice.image.file && notice.heroImagePolicyChecked.value === true) {
      notice.setHeroImagePolicyChecked(false);
      notice.heroImagePolicyChecked.setIsDirty(false);
    }
  }, [notice, notice.image.file]);

  // Clearing policy checkbox when there is no link in the body.
  useEffect(() => {
    if (
      !notice.hasLinkInBody &&
      notice.linksInBodyPolicyChecked.value === true
    ) {
      notice.setLinksInBodyPolicyChecked(false);
      notice.linksInBodyPolicyChecked.setIsDirty(false);
    }
  }, [notice, notice.hasLinkInBody]);

  // For a new update, seed the audience default. When the selector is shown the
  // user must choose explicitly, so start with nothing selected. When it is
  // hidden (single village without the Serviced Apartment feature) fall back to
  // the legacy independent-only default so an SA audience is never persisted.
  useEffect(() => {
    if (isEdit) {
      return;
    }
    notice.setTargetIndependent(!showAudienceSelection);
    notice.setTargetServiced(false);
    notice.targetIndependent.setIsDirty(false);
    notice.targetServiced.setIsDirty(false);
  }, [notice, isEdit, showAudienceSelection]);

  const radioGroupItems: ObservableList<RadioGroupKeyValue> =
    new ObservableList<RadioGroupKeyValue>(
      [
        new SimpleRadioGroupKeyValue(
          NoticePublishType.Now,
          t("notice:labels.publishNow"),
          "",
          false,
          t("notice:messages.publishNowHelpText")
        ),
        new DateTimePickerRadioGroupKeyValue(
          NoticePublishType.Scheduled,
          t("notice:labels.scheduledPublish"),
          notice.selectedPublishDate,
          zonedTimeToUtc(new Date(), timezone),
          timezone,
          notice.selectedPublishType === NoticePublishType.Scheduled,
          "",
          !!notice.publishDate && notice.publishDate < new Date(),
          LayoutOrientation.List,
          t("notice:messages.scheduledPublishPlaceholder"),
          t("notice:errors.scheduledPublishDate"),
          "top"
        )
      ],
      notice.selectedPublishType
    );

  const noticeTypeRadioGroupItems: ObservableList<RadioGroupKeyValue> =
    new ObservableList<RadioGroupKeyValue>(
      [
        new SimpleRadioGroupKeyValue(
          NoticeType.General,
          t("notice:labels.noticeType.general"),
          "",
          false
        ),
        new SimpleRadioGroupKeyValue(
          NoticeType.Important,
          t("notice:labels.noticeType.important"),
          "",
          false
        ),
        ...(accountStore.abilities.can("Create", "Announcement")
          ? [
              new SimpleRadioGroupKeyValue(
                NoticeType.Announcement,
                t("notice:labels.noticeType.announcement"),
                "",
                false
              )
            ]
          : [])
      ],
      notice.noticeType.text
    );

  const onSelectNoticeType = (item: RadioGroupKeyValue) => {
    runInAction(() => {
      notice.noticeType.setText(item.value as NoticeType);
    });
  };

  const onSelectPublishDateTime = (item: RadioGroupKeyValue) => {
    runInAction(() => {
      switch (item.value) {
        case NoticePublishType.Now:
          notice.selectedPublishType = NoticePublishType.Now;
          notice.selectedPublishDate.setDate(null);
          break;
        case NoticePublishType.Scheduled:
          notice.selectedPublishType = NoticePublishType.Scheduled;
          break;
      }
    });
  };

  const imageEditorOptions = {
    maxSizeInPixels: 1920
  };

  const breadcrumbItem: BreadcrumbItem = {
    name: t("translation:titles.notices"),
    onClick: () =>
      Navigation.navigateWithSelectedVillageId(history, RoutePaths.Notices),
    child: {name: isEdit ? notice.title.text : t("notice:titles.create")}
  };

  const onLinkClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
  };

  const combineTextAndLink = (text: string, link: string): JSX.Element => {
    return (
      <>
        {text}
        <a
          href="https://ryman.sharepoint.com/sites/library/Pages/Publishing-Photographs-and-Links.aspx"
          target="_blank"
          rel="noreferrer"
          onClick={onLinkClick}
        >
          {link}
        </a>
      </>
    );
  };

  return (
    <>
      <div className="notices">
        <CreateEditForm>
          <CreateEditForm.Header
            breadcrumbItem={breadcrumbItem}
            hasUnsavedChanges={hasUnsavedChanges}
          >
            {actionButtons}
          </CreateEditForm.Header>
          <CreateEditForm.Body>
            {isLoading ? (
              <Skeleton className="placeholder" />
            ) : (
              <>
                <div>
                  <TextEdit
                    type="text"
                    disabled={isLoading}
                    value={notice.title}
                    validationMessage={t("notice:errors.title")}
                    label={t("notice:labels.title")}
                    placeholder={t("notice:placeholders.title")}
                  />
                </div>

                {notice.isMultiVillage && (
                  <>
                    <Row className="form-separator editMargin" />
                    <Row className="editMargin">
                      <Col>
                        <MultiVillageSelect
                          villages={notice.villages}
                          allVillageOptionWarning={t(
                            "notice:messages.allVillageOptionSelectedNotice"
                          )}
                          invalidMinSelectedVillageErrorMessage={t(
                            "notice:errors.invalidMinimumVillageSelected"
                          )}
                        />
                      </Col>
                    </Row>
                  </>
                )}

                {showAudienceSelection && (
                  <div className="notice-audience">
                    <label className="audience-label">
                      {t("notice:labels.audienceTitle")}*
                    </label>
                    <span className="audience-subtitle">
                      <small>{t("notice:labels.audienceSubtitle")}</small>
                    </span>
                    <div className="audience-options">
                      <div className="audience-option">
                        <CheckBox
                          testId="targetIndependentCheckbox"
                          value={notice.targetIndependent}
                          label={t("notice:labels.targetIndependent")}
                          onValueChange={(value: boolean) =>
                            notice.setTargetIndependent(value)
                          }
                          disabled={isAudienceSelectionDisabled}
                        />
                      </div>
                      <div className="audience-option">
                        <CheckBox
                          testId="targetServicedCheckbox"
                          value={notice.targetServiced}
                          label={t("notice:labels.targetServiced")}
                          onValueChange={(value: boolean) =>
                            notice.setTargetServiced(value)
                          }
                          disabled={isAudienceSelectionDisabled}
                        />
                      </div>
                    </div>
                    {showAudienceError && (
                      <span className="error-message">
                        {t("notice:errors.audienceRequired")}
                      </span>
                    )}
                  </div>
                )}

                <div>
                  <ImagePreviewWithEditor
                    state={notice.image}
                    label={t("notice:labels.heroImage")}
                    options={imageEditorOptions}
                  />
                </div>

                {notice.image.file && (
                  <>
                    <CheckBox
                      testId="heroImagePolicyCheckbox"
                      value={notice.heroImagePolicyChecked}
                      label={combineTextAndLink(
                        t("notice:labels.imagePolicyText"),
                        t("notice:labels.policyLink")
                      )}
                      onValueChange={(value: boolean) =>
                        notice.setHeroImagePolicyChecked(value)
                      }
                      invalidErrorMessage={t("notice:errors.policyErrorImages")}
                      isRequired={true}
                    />
                    <div className="image-caption">
                      <TextEdit
                        type="text"
                        disabled={isLoading}
                        value={notice.imageCaption}
                        validationMessage={t("notice:errors.imageCaption")}
                        label={t("notice:labels.imageCaption")}
                        placeholder={t("notice:placeholders.imageCaption")}
                        helpText={t("notice:messages.imageCaptionHelpText")}
                      />
                    </div>
                  </>
                )}

                <div className="notice-body">
                  <RichTextEditor
                    disabled={isLoading}
                    text={notice.body}
                    validationMessage={t("notice:errors.body")}
                    label={t("notice:labels.body")}
                    placeholder={t("notice:placeholders.body")}
                  />
                </div>
                {notice.hasLinkInBody && (
                  <div>
                    <CheckBox
                      testId="linksPolicyCheckbox"
                      value={notice.linksInBodyPolicyChecked}
                      label={combineTextAndLink(
                        t("notice:labels.linksPolicyText"),
                        t("notice:labels.policyLink")
                      )}
                      onValueChange={(value: boolean) =>
                        notice.setLinksInBodyPolicyChecked(value)
                      }
                      invalidErrorMessage={t("notice:errors.policyErrorLink")}
                      isRequired={true}
                    />
                  </div>
                )}
                <div>
                  <TextEdit
                    type="text"
                    disabled={isLoading}
                    value={notice.author}
                    validationMessage={t("notice:errors.author")}
                    label={t("notice:labels.author")}
                    placeholder={t("notice:placeholders.author")}
                  />
                </div>

                <div className="form-separator" />
                <div className="notice-type-container">
                  <div className="notice-type-radiogroup">
                    <RadioGroup
                      items={noticeTypeRadioGroupItems}
                      name="noticeOptions"
                      isDisabled={isLoading}
                      onSelect={onSelectNoticeType}
                    />
                    <div className="helpText">
                      <small>{noticeTypeHelpText}</small>
                    </div>
                  </div>
                  <div className="send-notification-checkbox">
                    <CheckBox
                      data-testid="sendNotificationCheckbox"
                      value={notice.notificationEnabled.value}
                      label={t("notice:labels.sendNotification")}
                      onValueChange={notice.setIsNotificationEnabled}
                      helpText={t("notice:messages.sendNotificationHelpText")}
                      disabled={
                        isLoading ||
                        (isEdit &&
                          !!notice.notificationDate &&
                          notice.status !== NoticeStatus.Draft &&
                          notice.status !== NoticeStatus.Scheduled)
                      }
                    />
                  </div>
                </div>
                <div className="form-separator" />
                <div>
                  <DateTimePicker
                    disabled={isLoading}
                    label={t("notice:labels.expiryDate")}
                    helpText={t("notice:messages.expiryDateHelpText")}
                    value={notice.expiryDate}
                    timeZone={timezone}
                    isRequired={true}
                    dateTimePickerErrorMessage={
                      notice.expiryDate.date
                        ? t("notice:errors.expiryDate")
                        : t("notice:errors.expiryDateEmpty")
                    }
                    position="top"
                  />
                </div>
                <div className="form-separator" />
                <div>
                  <RadioGroup
                    items={radioGroupItems}
                    name="publishDateTime"
                    isDisabled={
                      isLoading ||
                      (isEdit && notice.status === NoticeStatus.Published)
                    }
                    onSelect={onSelectPublishDateTime}
                    orientation="grid"
                  />
                </div>
              </>
            )}
          </CreateEditForm.Body>
        </CreateEditForm>
      </div>
    </>
  );
};

export default observer(CreateEditNoticeTemplate);
